import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/auth-context'
import type { UserRole } from '../../types/auth.types'
import type { ReactNode } from 'react'

interface ProtectedRouteProps {
  children: ReactNode
  /** Required role(s) to access this route */
  requiredRoles?: UserRole[]
}

/**
 * Protected route wrapper that checks authentication and role-based access.
 * Redirects to login if not authenticated.
 * Shows access denied if user lacks required role.
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, userRole } = useAuth()
  const location = useLocation()

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Client users → redirect to portal (staff routes are not for clients)
  if (userRole === 'client') {
    return <Navigate to="/portal" replace />
  }

  // Check role-based access if required
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = userRole && requiredRoles.includes(userRole)

    if (!hasRequiredRole) {
      return (
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p>Required role: {requiredRoles.join(' or ')}</p>
          <p>Your role: {userRole || 'none'}</p>
        </div>
      )
    }
  }

  return <>{children}</>
}
